import { FimMode, type FimPath } from '@bartizo/shared';
import type { ReactNode } from 'react';
import { Button } from '../ui/Button.js';
import { SelectField, TextField } from '../ui/Field.js';
import { MODE_LABELS } from './settings-fields.js';

/**
 * The paths Wazuh watches on a layer (knowledge doc section 8.2):
 * `wazuh_fim.paths`, one row per path with the mode it is watched in.
 *
 * Structured fields, because section 11 rules out raw config text and a watched
 * path is two things: where, and how. The agent writes them into Wazuh's
 * `ossec.conf` through the root helper (P7.3), which is also why the path is
 * refused, not escaped, when it holds anything that is not part of a path —
 * `SettingsPatch` at the API says which character and why.
 *
 * Paths merge by path across layers (`LIST_MERGE`): a path a role or a wider
 * layer watches is watched here too, and naming it again only changes its mode.
 */

export interface FimPathListEditorProps {
  value: FimPath[];
  onChange: (next: FimPath[]) => void;
  disabled: boolean;
}

export function FimPathListEditor({
  value,
  onChange,
  disabled,
}: FimPathListEditorProps): ReactNode {
  const replace = (index: number, next: FimPath): void => {
    onChange(value.map((entry, i) => (i === index ? next : entry)));
  };

  return (
    <div className="target-list">
      {value.length === 0 && <p className="muted text-sm">No paths set in this layer.</p>}

      {value.map((entry, index) => {
        const title = entry.path === '' ? `Path ${String(index + 1)}` : entry.path;
        return (
          <fieldset key={index} className="target" disabled={disabled}>
            <legend>{title}</legend>
            <div className="target-grid">
              <TextField
                label="Path"
                hint="Absolute. * and ? match, as in /home/*/.ssh."
                value={entry.path}
                maxLength={1024}
                spellCheck={false}
                placeholder="/var/www/uploads"
                onChange={(event) => {
                  // Not trimmed as it is typed: a space inside a path is allowed, and
                  // one at either end is refused at the API with the reason.
                  replace(index, { ...entry, path: event.target.value });
                }}
              />
              <SelectField
                label="Mode"
                hint="A schedule is every 12 hours on a Wazuh the installer set up. Who did it needs the Linux audit daemon; without it Wazuh watches in real time."
                value={entry.mode}
                onChange={(event) => {
                  const mode = FimMode.safeParse(event.target.value);
                  if (mode.success) replace(index, { ...entry, mode: mode.data });
                }}
              >
                {FimMode.options.map((mode) => (
                  <option key={mode} value={mode}>
                    {MODE_LABELS[mode]}
                  </option>
                ))}
              </SelectField>
            </div>
            <div className="row">
              <Button
                variant="ghost"
                onClick={() => {
                  onChange(value.filter((_, i) => i !== index));
                }}
              >
                Remove {title}
              </Button>
            </div>
          </fieldset>
        );
      })}

      <div className="row">
        <Button
          disabled={disabled}
          onClick={() => {
            onChange([...value, { path: '', mode: 'realtime' }]);
          }}
        >
          Add a path
        </Button>
      </div>
    </div>
  );
}
